/**
 * TransferProgressStep - Step 6: Resultado da execução da transferência
 * 
 * Mostra o resultado final da transferência:
 * - Sucesso: Quantidade transferida
 * - Erro: Mensagem de erro
 */

import { TransferType, TransferExecutionResult, TRANSFER_TYPE_LABELS } from '@/hooks/useBulkTransfer';
import { Card, CardContent } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { 
  CheckCircle2, 
  XCircle, 
  Loader2,
  ArrowRightLeft,
  Ban
} from 'lucide-react';
import { cn } from '@/lib/utils';

// =============================================================================
// TIPOS
// =============================================================================

interface TransferProgressStepProps {
  transferType: TransferType;
  executionResult: TransferExecutionResult | null;
  isLoading: boolean;
  error: string | null;
}

// =============================================================================
// COMPONENTE
// =============================================================================

export function TransferProgressStep({
  transferType,
  executionResult,
  isLoading,
  error,
}: TransferProgressStepProps) {
  // =========================================================================
  // ESTADO: EXECUTANDO
  // =========================================================================

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center justify-center">
            <ArrowRightLeft className="h-8 w-8 text-primary animate-pulse" />
          </div>
          <Loader2 className="h-16 w-16 animate-spin text-primary/30" />
        </div>
        
        <h3 className="text-lg font-semibold mb-2">Transferindo registros...</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Aguarde enquanto os registros são transferidos
        </p>
        
        <div className="w-full max-w-xs">
          <Progress value={undefined} className="h-2" />
        </div>
        
        <p className="text-xs text-muted-foreground mt-4">
          Esta operação é atômica. Em caso de erro, todos os registros serão revertidos.
        </p>
      </div>
    );
  }

  // =========================================================================
  // ESTADO: ERRO
  // =========================================================================

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className="p-4 rounded-full bg-destructive/10 mb-4">
          <XCircle className="h-12 w-12 text-destructive" />
        </div>
        
        <h3 className="text-lg font-semibold text-destructive mb-2">
          Erro na transferência
        </h3>
        
        <Alert variant="destructive" className="mt-4">
          <XCircle className="h-4 w-4" />
          <AlertTitle>Operação falhou</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        
        <p className="text-sm text-muted-foreground mt-4 text-center">
          Nenhum registro foi alterado. A operação foi revertida automaticamente.
        </p>
      </div>
    );
  }

  // =========================================================================
  // ESTADO: SUCESSO
  // =========================================================================

  if (executionResult) {
    const hasTransferred = executionResult.transferred > 0;
    const hasCancelledTransfers = (executionResult.cancelled_transfers || 0) > 0;
    // v2.3.0: Verificar se foi distribuição via fila
    const isQueueMode = executionResult.target_mode === 'queue';
    const distributedCount = executionResult.distributed_via_queue || 0;

    return (
      <div className="flex flex-col items-center justify-center py-8">
        <div className={cn(
          "p-4 rounded-full mb-4",
          hasTransferred ? "bg-green-500/10" : "bg-amber-500/10"
        )}>
          {hasTransferred ? (
            <CheckCircle2 className="h-12 w-12 text-green-600" />
          ) : (
            <Ban className="h-12 w-12 text-amber-600" />
          )}
        </div>
        
        <h3 className={cn(
          "text-lg font-semibold mb-2",
          hasTransferred ? "text-green-600" : "text-amber-600"
        )}>
          {hasTransferred 
            ? (isQueueMode ? 'Distribuição concluída!' : 'Transferência concluída!') 
            : 'Nenhum registro transferido'}
        </h3>
        
        <p className="text-sm text-muted-foreground mb-6">
          {TRANSFER_TYPE_LABELS[transferType]}
          {isQueueMode && ' (via fila de distribuição)'}
        </p>

        {/* Cards de estatísticas */}
        <div className={cn(
          "grid gap-4 w-full max-w-sm",
          isQueueMode ? "grid-cols-2" : "grid-cols-2"
        )}>
          <Card className={cn(
            "border-2",
            hasTransferred && "border-green-500/50 bg-green-500/5"
          )}>
            <CardContent className="p-4 text-center">
              <p className={cn(
                "text-3xl font-bold",
                hasTransferred ? "text-green-600" : "text-muted-foreground"
              )}>
                {isQueueMode ? distributedCount : executionResult.transferred}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {isQueueMode ? 'Distribuídos' : 'Transferidos'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-muted-foreground">
                {executionResult.skipped}
              </p>
              <p className="text-xs text-muted-foreground mt-1">Ignorados</p>
            </CardContent>
          </Card>
        </div>

        {/* Aviso sobre transferências canceladas */}
        {hasCancelledTransfers && (
          <Alert className="mt-4 max-w-sm border-amber-500/50 bg-amber-500/10">
            <AlertTitle className="text-amber-600">
              {executionResult.cancelled_transfers} transferência(s) pendente(s) cancelada(s)
            </AlertTitle>
            <AlertDescription>
              As transferências anteriores foram substituídas por esta nova transferência.
            </AlertDescription>
          </Alert>
        )}

        {/* ID do log para referência */}
        {executionResult.log_id && (
          <p className="text-xs text-muted-foreground mt-4">
            ID da operação: {executionResult.log_id.substring(0, 8)}...
          </p>
        )}
      </div>
    );
  }

  // Estado inicial (não deveria chegar aqui)
  return null;
}
